import { Injectable } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import { FULLY_UNAUTHENTICATED_APIS } from '../constants/unauthenticated-apis';
import { AuthenticationService } from '../contracts/authentication-service.contract';
import { AuthenticationResponse, HealthStatus } from '../models/ApiResponse';

import { getDomain } from '@innovation-os-ng/core/utils';
import { RestApiService } from '../stubs/rest-api-service.stub';

/**
 * Provides methods to authenticate or un-authenticate a user.
 */
@Injectable()
export class ApiAuthenticationService extends RestApiService implements AuthenticationService {
  // Logout API
  private AUTH_LOGOUT_URI: string = '/auth/authenticate/logout';
  /**
   * Authenticates a user given email address and password.
   *
   * @throws AuthenticationInvalidException
   * @throws PasswordResetRequiredException
   * @throws UserNotFoundException
   *
   * @param email The email address of the user.
   * @param password The password of the user.
   */
  public async login(email: string, password: string, captchaSolution?: string): Promise<AuthenticationResponse> {
    try {
      const response = await firstValueFrom(
        this.httpClient.post<AuthenticationResponse>(`${this.restUrl}${FULLY_UNAUTHENTICATED_APIS.AUTH_LOGIN_URI}`, {
          email,
          password,
          captchaSolution
        })
      );
      return response;
    } catch (error) {
      this.convertToException(error);
    }
  }

    /**
   * Authenticates a user given email address and password.
   *
   * @throws AuthenticationInvalidException
   * @throws PasswordResetRequiredException
   * @throws UserNotFoundException
   *
   * @param email The email address of the user.
   * @param password The password of the user.
   */
    public loginObs(email: string, password: string, captchaSolution?: string): Observable<AuthenticationResponse> {
      return this.httpClient.post<AuthenticationResponse>(`${this.restUrl}${FULLY_UNAUTHENTICATED_APIS.AUTH_LOGIN_URI}`, {
        email,
        password,
        captchaSolution
      })
    }

  /**
   * Check the health status of the login. If there are multiple failed login attempts, showCaptcha = true will be returned.
   */
  public async getHealthStatus(): Promise<HealthStatus> {
    try {
      const response = (await firstValueFrom(
        this.httpClient.get(this.environmentService.getRestUrl(FULLY_UNAUTHENTICATED_APIS.LOGIN_HEALTH_STATUS_URL))
      )) as HealthStatus;
      return response;
    } catch (error) {
      this.convertToException(error);
    }
  }

  /**
   * Logs a user out globaly.
   *
   * @param authToken Authentication Token of the user.
   * @param refreshToken Refresh Token of the user.
   * @param tenantSlug Is an optional parameter used only for SSO tenants.
   */
  public async logout(authToken: string, refreshToken: string, tenantSlug?: string): Promise<string> {
    // Referer header parameter is only set for SSO tenants
    const headers = tenantSlug
      ? {
          Referer: getDomain()
        }
      : undefined;

    try {
      return await firstValueFrom<string>(
        this.httpClient.post<string>(
          `${this.restUrl}${this.AUTH_LOGOUT_URI}`,
          {
            tenantSlug,
            refreshToken,
            authToken
          },
          { headers }
        )
      );
    } catch (error) {
      this.convertToException(error);
    }
  }
}
